// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: where the voice mode globe sits, as big picture cards like the Appearance theme cards:
// centred (above the phone rail, above the composer on desktop) or in place of the Ask button
// (the rail's Ask pill on phones, the corner assistant on desktop). Saved on this device.

import {SettingsChoiceCards} from '@app/features/app/components/dialogs/shared/SettingsChoiceCards';
import styles from '@app/features/konvo_assistant/components/VoiceModePlacementSetting.module.css';
import AssistantSpeech, {type VoicePlacement} from '@app/features/konvo_assistant/state/AssistantSpeech';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const VIOLET = [153, 129, 244];
const PINK = [226, 150, 196];
const PEACH = [255, 161, 111];

/** The globe of dots, frozen mid-turn: the same golden-spiral ball voice mode draws. */
const GLOBE_DOTS = (() => {
	const n = 30;
	const tilt = 0.45;
	const spin = 0.7;
	const dots: Array<{x: number; y: number; r: number; fill: string}> = [];
	for (let i = 0; i < n; i++) {
		const y = 1 - ((i + 0.5) / n) * 2;
		const ring = Math.sqrt(1 - y * y);
		const th = i * 2.399963;
		const x0 = Math.cos(th) * ring;
		const z0 = Math.sin(th) * ring;
		const x = x0 * Math.cos(spin) + z0 * Math.sin(spin);
		const z1 = -x0 * Math.sin(spin) + z0 * Math.cos(spin);
		const py = y * Math.cos(tilt) - z1 * Math.sin(tilt);
		const z = y * Math.sin(tilt) + z1 * Math.cos(tilt);
		const u = (x + 1) / 2;
		const [a, b, k] = u < 0.5 ? [VIOLET, PINK, u * 2] : [PINK, PEACH, (u - 0.5) * 2];
		const rgb = a.map((v, j) => Math.round(v + (b[j]! - v) * k));
		dots.push({
			x: x * 10,
			y: py * 9,
			r: 1.1 * (0.55 + (0.9 * (z + 1)) / 2),
			fill: `rgba(${rgb.join(',')},${(0.25 + (0.75 * (z + 1)) / 2).toFixed(2)})`,
		});
	}
	return dots.sort((p, q) => p.r - q.r);
})();

function Globe({className}: {className?: string}) {
	return (
		<svg className={clsx(styles.globe, className)} viewBox="-13 -13 26 26" aria-hidden>
			{GLOBE_DOTS.map((dot, index) => (
				<circle key={index} cx={dot.x} cy={dot.y} r={dot.r} fill={dot.fill} />
			))}
		</svg>
	);
}

/** What was said, as two lines of type above (or beside) the globe. */
function Words({className}: {className?: string}) {
	return (
		<span className={clsx(styles.words, className)} aria-hidden>
			<i className={styles.wordsYou} />
			<i className={styles.wordsKonvo} />
		</span>
	);
}

function PhoneArt({placement}: {placement: VoicePlacement}) {
	return (
		<span className={clsx(styles.art, styles.phone, placement === 'ask' && styles.atAsk)} aria-hidden>
			<span className={styles.rows}>
				{[0, 1, 2].map((row) => (
					<span key={row} className={styles.row}>
						<i className={styles.avatar} />
						<span className={styles.lines}>
							<i />
							<i />
						</span>
					</span>
				))}
			</span>
			<span className={styles.fade} />
			<Words className={styles.phoneWords} />
			<span className={styles.rail}>
				<i className={styles.tab} />
				<i className={styles.tab} />
				<i className={styles.tab} />
				<i className={styles.ask} />
			</span>
			<Globe className={styles.phoneGlobe} />
		</span>
	);
}

function DesktopArt({placement}: {placement: VoicePlacement}) {
	return (
		<span className={clsx(styles.art, styles.desktop, placement === 'ask' && styles.atAsk)} aria-hidden>
			<span className={styles.sidebar}>
				<i />
				<i />
				<i />
				<i />
			</span>
			<span className={styles.messages}>
				<i />
				<i />
				<i />
			</span>
			<span className={styles.composer} />
			<span className={styles.fade} />
			<Words className={styles.desktopWords} />
			<i className={styles.corner} />
			<Globe className={styles.desktopGlobe} />
		</span>
	);
}

export const VoiceModePlacementSetting = observer(() => {
	const {i18n} = useLingui();
	const mobile = MobileLayout.enabled;
	const Art = mobile ? PhoneArt : DesktopArt;
	return (
		<div className={styles.setting} data-flx="konvo-assistant.voice-placement">
			<SettingsChoiceCards<VoicePlacement>
				aria-label={i18n._(msg`Voice mode position`)}
				value={AssistantSpeech.voicePlacementFor(mobile)}
				onChange={AssistantSpeech.setVoicePlacement}
				options={[
					{
						value: 'centre',
						name: i18n._(msg({message: 'Centred', comment: 'Voice mode position setting: the globe floats centred.'})),
						desc: mobile
							? i18n._(msg`The globe floats in the middle, just above the bar.`)
							: i18n._(msg`The globe floats in the middle, just above where you type.`),
						preview: <Art placement="centre" />,
					},
					{
						value: 'ask',
						name: i18n._(
							msg({
								message: 'In place of Ask',
								comment: 'Voice mode position setting: the Ask button itself turns into the voice globe.',
							}),
						),
						desc: mobile
							? i18n._(msg`The Ask button turns into the globe.`)
							: i18n._(msg`The corner assistant turns into the globe.`),
						preview: <Art placement="ask" />,
					},
				]}
				data-flx="konvo-assistant.voice-placement.cards"
			/>
			<p className={styles.hint}>
				<Trans>Where the globe sits while you talk to Konvo. Saved on this device.</Trans>
			</p>
		</div>
	);
});
